/*
 * Правило повтора встречи: модель окна повтора и его человеческое описание
 * («Что получилось» внизу окна — образец Яндекс.Календаря).
 *
 * Дни недели нумеруются как на сервере (recurrence.ts): 1 — понедельник,
 * 7 — воскресенье.
 */

export type RepeatFreq = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';

export type RepeatRule = {
  freq: RepeatFreq;
  interval: number;
  days: number[];
  /** «ГГГГ-ММ-ДД» или пустая строка — повторять всегда. */
  until: string;
};

export const WEEKDAYS: { n: number; short: string; acc: string }[] = [
  { n: 1, short: 'Пн', acc: 'понедельник' },
  { n: 2, short: 'Вт', acc: 'вторник' },
  { n: 3, short: 'Ср', acc: 'среду' },
  { n: 4, short: 'Чт', acc: 'четверг' },
  { n: 5, short: 'Пт', acc: 'пятницу' },
  { n: 6, short: 'Сб', acc: 'субботу' },
  { n: 7, short: 'Вс', acc: 'воскресенье' },
];

export const FREQ_LABEL: Record<RepeatFreq, string> = {
  NONE: 'Не повторять',
  DAILY: 'Каждый день',
  WEEKLY: 'Каждую неделю',
  MONTHLY: 'Каждый месяц',
};

/** Единица шага повтора для строки «Повторять каждые N …». */
export const UNIT_LABEL: Record<Exclude<RepeatFreq, 'NONE'>, [string, string, string]> = {
  DAILY: ['день', 'дня', 'дней'],
  WEEKLY: ['неделю', 'недели', 'недель'],
  MONTHLY: ['месяц', 'месяца', 'месяцев'],
};

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

const untilFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

const dayList = (days: number[]): string => {
  const names = [...days]
    .sort((a, b) => a - b)
    .map((n) => WEEKDAYS.find((w) => w.n === n)?.acc)
    .filter((x): x is string => !!x);
  if (!names.length) return '';
  // «во вторник», но «в среду» — предлог смотрит на первое слово
  const prep = names[0].startsWith('вт') ? 'во' : 'в';
  const head = names.slice(0, -1).join(', ');
  const tail = names[names.length - 1];
  return `${prep} ${head ? `${head} и ${tail}` : tail}`;
};

/** Человеческое описание правила: строка «Что получилось» внизу окна повтора. */
export function repeatSummary(rule: RepeatRule): string {
  if (rule.freq === 'NONE') return 'Не повторять';
  const n = Math.max(1, Math.floor(rule.interval) || 1);
  const [one, few, many] = UNIT_LABEL[rule.freq];
  const every =
    n === 1
      ? { DAILY: 'Повторять каждый день', WEEKLY: 'Повторять каждую неделю', MONTHLY: 'Повторять каждый месяц' }[
          rule.freq
        ]
      : `Повторять каждые ${n} ${plural(n, one, few, many)}`;

  let text = every;
  if (rule.freq === 'WEEKLY' && rule.days.length) {
    const list = dayList(rule.days);
    // «Повторять в субботу» — при обычном шаге неделя и так подразумевается
    text = n === 1 ? `Повторять ${list}` : `${every} ${list}`;
  }

  if (rule.until) {
    const [y, m, d] = rule.until.split('-').map(Number);
    if (y && m && d) text += `, до ${untilFmt.format(new Date(y, m - 1, d))}`;
  }
  return text;
}
